import React, { useState, useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import { useForm, Controller } from "react-hook-form"
import "./CustomDropdown.css"

type DropdownOption = {
  value: string
  label: string
}

interface CustomDropdownProps {
  name: string
  control: any
  options: DropdownOption[]
  defaultValue?: string
  rules?: any
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  name,
  control,
  options,
  defaultValue = null,
  rules,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const dropdownListRef = useRef<HTMLUListElement | null>(null)

  const updatePosition = () => {
    if (dropdownRef.current && dropdownListRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
      const position = ({
        top: rect.bottom + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width,
      })
      dropdownListRef.current.style.top = `${position.top}px`
      dropdownListRef.current.style.left = `${position.left}px`
      dropdownListRef.current.style.width = `${position.width}px`
      dropdownListRef.current.style.position = 'absolute'
    }
  }

  const toggleDropdown = () => {
    if (!isOpen) {
      updatePosition()
    }
    setIsOpen(!isOpen)
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      isOpen &&
      !dropdownRef.current?.contains(event.target as Node) &&
      !dropdownListRef.current?.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick)
      window.addEventListener("resize", updatePosition)
      window.addEventListener("scroll", updatePosition) // Use capture phase to catch scrolls earlier
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition)
    }
  }, [isOpen])

  useEffect(() => {
    let animationFrameId: number
    const refreshPosition = () => {
      updatePosition()
      animationFrameId = requestAnimationFrame(refreshPosition)
    }
    refreshPosition()
    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => {
        const DropdownList = () => (
          <ul className="dropdown-list" ref={dropdownListRef} >
            {options.map((option) => (
              <li
                key={option.value}
                onClick={() => {
                  setIsOpen(false)
                  field.onChange(option.value)
                }}
                className={`dropdown-item ${option.value === field.value ? "selected" : ""}`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )

        return (
          <div ref={dropdownRef} className="dropdown-parent">
            <div className="dropdown-container" onClick={toggleDropdown}>
              {field.value ? options.find((option) => option.value === field.value)?.label : "Select..."}
              <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
            </div>
            {isOpen && ReactDOM.createPortal(<DropdownList />, document.body)} 
          </div>
        )
      }}
    />
  )
}

export default CustomDropdown